import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {alpha, useTheme} from "@mui/material/styles";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";
import {chartPalette} from "../../components/chartColors";
import {InsightsLegend, LegendEntry} from "../../kit/InsightsLegend";
import {InsightsColumn, InsightsTable} from "../../kit/InsightsTable";
import {InsightsTooltipPanel, TooltipRow, TooltipTitle} from "../../kit/InsightsTooltip";
import {SeriesCard} from "../../kit/SeriesCard";
import {CHART_BAR_RADIUS, CHART_DRAW_DURATION, CHART_HEIGHT} from "../../kit/insightsChart";
import type {InsightsLoading} from "../../kit/insightsLoading";

const COMPARE_ALPHA = 0.35;
const MAX_BAR_SIZE = 40;

/** The part id a card that measures one thing per bucket files its figure under. */
const MEASURE = "value";

const comparedKey = (partId: string) => `${partId}__compare`;

const TOP_RADIUS: [number, number, number, number] = [CHART_BAR_RADIUS, CHART_BAR_RADIUS, 0, 0];
const FLAT_RADIUS: [number, number, number, number] = [0, 0, 0, 0];

/** A segment of the bar. A card that measures one thing has a single part and names it nowhere. */
export interface BucketPart {
    id: string;
    label: string;
}

/** One bar: the bucket's axis label and its figure under every part id, in each period. */
export interface BucketRow {
    key: string;
    label: string;
    values: Record<string, number>;
    compareValues?: Record<string, number>;
}

/** The rows for the cards that measure one thing per bucket, which is the shape the API returns. */
export function bucketRows(
    items: ReadonlyArray<{key: string; value: number; compareValue?: number}>,
    label: (key: string) => string,
): BucketRow[] {
    return items.map(item => ({
        key: item.key,
        label: label(item.key),
        values: {[MEASURE]: item.value},
        compareValues: item.compareValue == null ? undefined : {[MEASURE]: item.compareValue},
    }));
}

/** Past this many characters a bucket name runs into its neighbour, so the whole axis breaks in two. */
const WRAP_AT = 10;

/**
 * A range breaks at its dash, which stays with the upper bound — "€5K" over "–€15K" reads as one range,
 * where "€5K–" over "€15K" reads as two figures. Anything else breaks at the space nearest the middle,
 * so neither line is left carrying the label on its own.
 */
function tickLines(label: string, wrap: boolean): string[] {
    if (!wrap) return [label];

    const dash = label.indexOf("–");

    if (dash > 0) return [label.slice(0, dash), label.slice(dash)];

    const middle = label.length / 2;
    let at = -1;

    for (let index = label.indexOf(" "); index >= 0; index = label.indexOf(" ", index + 1)) {
        if (at < 0 || Math.abs(index - middle) < Math.abs(at - middle)) at = index;
    }

    return at <= 0 ? [label] : [label.slice(0, at), label.slice(at + 1)];
}

interface BucketTickProps {
    x?: number;
    y?: number;
    payload?: {value?: string};
    fill: string;
    wrap: boolean;
    highlighted?: string;
}

/** The bucket the reader is standing in is marked, so it is not read as just another bucket. */
function BucketTick({x, y, payload, fill, wrap, highlighted}: BucketTickProps) {
    const value = payload?.value ?? "";
    const isCurrent = highlighted != null && value === highlighted;

    return (
        <text
            x={x}
            y={y}
            textAnchor={"middle"}
            fill={fill}
            fontSize={12}
            fontWeight={isCurrent ? 700 : 400}
            textDecoration={isCurrent ? "underline" : undefined}
        >
            {tickLines(value, wrap).map((line, index) => (
                <tspan key={line} x={x} dy={index === 0 ? 12 : 14}>{line}</tspan>
            ))}
        </text>
    );
}

interface HoveredBucket {
    title: string;
    rows: Array<{swatch?: string; label: string; value: string}>;
}

interface BucketTooltipProps {
    active?: boolean;
    payload?: Array<{payload?: {key?: unknown}}>;
    describe: (key: string) => HoveredBucket | undefined;
}

function BucketTooltip({active, payload, describe}: BucketTooltipProps) {
    const hovered = active ? describe(String(payload?.[0]?.payload?.key ?? "")) : undefined;

    if (hovered == null) {
        return null;
    }

    return (
        <InsightsTooltipPanel>
            <TooltipTitle title={hovered.title}/>

            {hovered.rows.map(row => (
                <TooltipRow key={row.label} swatch={row.swatch} label={row.label} value={row.value}/>
            ))}
        </InsightsTooltipPanel>
    );
}

interface Props {
    title: string;
    hint: string;
    rows: BucketRow[];
    /** What the buckets are, for the table's first column; on most cards the title already says it. */
    bucketLabel?: string;
    /** The segments the bar stacks, in draw order. A card that measures one thing leaves this out. */
    parts?: BucketPart[];
    loading?: InsightsLoading;
    comparing?: boolean;
    /** How one figure reads in full — "36 guests", "12.3%". */
    format: (value: number) => string;
    /** The same figure shortened for the value axis. */
    formatTick: (value: number) => string;
    /** What the figure is, for the table and the hover; a bar with parts lets the parts name themselves. */
    valueLabel?: string;
    /** The bucket to mark on the axis — the calendar month we are standing in. */
    highlighted?: string;
    empty: string;
}

/**
 * Buckets that come in an order the reader already knows — party sizes, age bands, calendar months, distance
 * bands — drawn as the vertical bars that order deserves. The comparison period stands beside each bar rather
 * than under it, because two bars of the same width are what makes two periods comparable at a glance.
 */
export function CrmBucketsCard({
    title,
    hint,
    rows,
    bucketLabel,
    parts,
    loading,
    comparing = false,
    format,
    formatTick,
    valueLabel,
    highlighted,
    empty,
}: Props) {
    const {t} = useTranslation();
    const theme = useTheme();

    const totalLabel = t("Total");
    const compareLabel = t("Comparison period");

    // A card that measures one thing still has a part; it is simply the only one, so it needs no name of its own.
    const drawn: BucketPart[] = parts ?? [{id: MEASURE, label: t("Selected period")}];
    const stacked = drawn.length > 1;

    // The theme's own colour heads the palette, so one series is the property's colour and no colour is passed in.
    const palette = chartPalette(theme);
    const colorOf = (index: number) => palette[index % palette.length];
    const compareColor = alpha(colorOf(0), COMPARE_ALPHA);

    const sum = (values?: Record<string, number>) =>
        drawn.reduce((total, part) => total + (values?.[part.id] ?? 0), 0);

    const hasCompare = comparing && rows.some(row => row.compareValues != null);
    const hasData = rows.some(row => sum(row.values) > 0 || sum(row.compareValues) > 0);

    const plotRows = rows.map(row => {
        const plot: Record<string, string | number | null> = {key: row.key, label: row.label};

        drawn.forEach(part => {
            plot[part.id] = row.values[part.id] ?? 0;
            plot[comparedKey(part.id)] = row.compareValues?.[part.id] ?? null;
        });

        return plot;
    });

    const rowByKey = new Map(rows.map(row => [row.key, row]));
    const wrap = rows.some(row => row.label.length > WRAP_AT);

    const describe = (key: string): HoveredBucket | undefined => {
        const row = rowByKey.get(key);

        if (row == null) return undefined;

        return {
            title: row.label,
            rows: [
                ...drawn.map((part, index) => ({
                    swatch: colorOf(index),
                    label: stacked ? part.label : valueLabel ?? totalLabel,
                    value: format(row.values[part.id] ?? 0),
                })),
                ...(stacked ? [{label: totalLabel, value: format(sum(row.values))}] : []),
                ...(hasCompare && row.compareValues != null
                    ? [{swatch: compareColor, label: compareLabel, value: format(sum(row.compareValues))}]
                    : []),
            ],
        };
    };

    // Re-running the draw on every hover would be noise; it plays once per change of what is charted.
    const [animate, setAnimate] = React.useState(true);
    const shape = `${rows.length}:${drawn.length}:${hasCompare}`;

    React.useEffect(() => {
        setAnimate(true);
        const timer = window.setTimeout(() => setAnimate(false), CHART_DRAW_DURATION + 400);

        return () => window.clearTimeout(timer);
    }, [shape]);

    const columns: InsightsColumn<BucketRow>[] = [
        {key: "label", label: bucketLabel ?? title, kind: "label", cell: row => row.label},
        ...drawn.map(part => ({
            key: part.id,
            label: stacked ? part.label : valueLabel ?? totalLabel,
            kind: "value" as const,
            cell: (row: BucketRow) => format(row.values[part.id] ?? 0),
        })),
        ...(stacked ? [{
            key: "total",
            label: valueLabel ?? totalLabel,
            kind: "value" as const,
            cell: (row: BucketRow) => format(sum(row.values)),
        }] : []),
        ...(hasCompare ? [{
            key: "compare",
            label: compareLabel,
            kind: "compare" as const,
            cell: (row: BucketRow) => row.compareValues == null ? "—" : format(sum(row.compareValues)),
        }] : []),
    ];

    // One entry would only name the single thing on the chart, which the title already did.
    const legendEntries = drawn.length + (hasCompare ? 1 : 0);

    return (
        <SeriesCard
            title={title}
            hint={hint}
            loading={loading}
            empty={hasData ? undefined : (
                <Stack alignItems={"center"} justifyContent={"center"} sx={{height: CHART_HEIGHT, px: 3}}>
                    <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"}>
                        {empty}
                    </Typography>
                </Stack>
            )}
            legend={legendEntries < 2 ? undefined : (
                <InsightsLegend>
                    {drawn.map((part, index) => (
                        <LegendEntry key={part.id} shape={"bar"} color={colorOf(index)} label={part.label}/>
                    ))}

                    {hasCompare && <LegendEntry shape={"bar"} color={compareColor} label={compareLabel}/>}
                </InsightsLegend>
            )}
            table={<InsightsTable label={title} columns={columns} rows={rows} rowKey={row => row.key}/>}
            chart={(
                <Box sx={{width: "100%", minWidth: 0, height: CHART_HEIGHT}}>
                    <ResponsiveContainer width={"100%"} height={"100%"} minWidth={0}>
                        <BarChart data={plotRows} margin={{top: 8, right: 8, bottom: 0, left: 0}} barGap={1}>
                            <CartesianGrid vertical={false} stroke={theme.palette.divider} strokeOpacity={0.8}/>

                            <XAxis
                                dataKey={"label"}
                                axisLine={false}
                                tickLine={false}
                                tickMargin={12}
                                height={wrap ? 48 : 34}
                                interval={0}
                                tick={(
                                    <BucketTick
                                        fill={theme.palette.text.secondary}
                                        wrap={wrap}
                                        highlighted={highlighted}
                                    />
                                )}
                            />

                            <YAxis
                                width={48}
                                axisLine={false}
                                tickLine={false}
                                tickMargin={8}
                                domain={[0, "auto"]}
                                tick={{fill: theme.palette.text.secondary, fontSize: 11}}
                                tickFormatter={formatTick}
                            />

                            <RechartsTooltip
                                isAnimationActive={false}
                                offset={14}
                                cursor={{fill: alpha(theme.palette.text.primary, 0.05)}}
                                wrapperStyle={{pointerEvents: "none", transition: "none", outline: "none", zIndex: 10}}
                                content={<BucketTooltip describe={describe}/>}
                            />

                            {drawn.map((part, index) => (
                                <Bar
                                    key={part.id}
                                    dataKey={part.id}
                                    stackId={stacked ? "selected" : undefined}
                                    fill={colorOf(index)}
                                    radius={stacked && index < drawn.length - 1 ? FLAT_RADIUS : TOP_RADIUS}
                                    maxBarSize={MAX_BAR_SIZE}
                                    isAnimationActive={animate}
                                    animationDuration={CHART_DRAW_DURATION}
                                    animationEasing={"ease-out"}
                                />
                            ))}

                            {hasCompare && drawn.map((part, index) => (
                                <Bar
                                    key={comparedKey(part.id)}
                                    dataKey={comparedKey(part.id)}
                                    stackId={stacked ? "compared" : undefined}
                                    fill={alpha(colorOf(index), COMPARE_ALPHA)}
                                    radius={stacked && index < drawn.length - 1 ? FLAT_RADIUS : TOP_RADIUS}
                                    maxBarSize={MAX_BAR_SIZE}
                                    isAnimationActive={animate}
                                    animationDuration={CHART_DRAW_DURATION}
                                    animationEasing={"ease-out"}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        />
    );
}

export default CrmBucketsCard;
